import type { Patrol, StartPatrolRequest } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncGeolocation } from '../../../hooks/use-async-geolocation.js';
import { useLocalStorage } from '../../../hooks/use-local-storage.js';
import { useVehicleRegister } from '../../../hooks/use-vehicle-register.js';
import { useAuthenticated } from '../../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../../utils/types.js';
import { formikCheck } from '../../../utils/validation.js';
import type { PreviousVehicles } from '../recent-vehicles.js';
import type { StartPatrolForm } from './start.view.js';
import {
  StartPatrolView,
  startPatrolFormContract,
  startPatrolFormContractNoVehicle,
} from './start.view.js';
import { roundTimeToMinute } from './view-utils.js';

export const StartPatrolContent: FC<ExistingEntityViewProps<Patrol.Type>> = ({
  data: patrol,
  id,
  refresh,
}) => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const [previousVehicles, setPreviousVehicles] = useLocalStorage<
    Record<string, PreviousVehicles>
  >('vehicle-history', {});
  const { enqueueSnackbar } = useSnackbar();
  const { isGeolocationAvailable, isGeolocationEnabled } = useAsyncGeolocation({
    watchLocationPermissionChange: true,
  });

  const recentVehicles = useMemo(() => {
    const values = Object.values(previousVehicles);
    values.sort((a, b) => b.lastUsed - a.lastUsed);
    return values.map((value) => value.vehicle).slice(0, 5);
  }, [previousVehicles]);

  const [combinedVehicles] = useVehicleRegister(recentVehicles);

  const acceptedPatrollers = useMemo(() => {
    const patrollers = patrol.patrollers || {};
    return Object.keys(patrollers).filter((patrollerId) => {
      const assignment = patrollers[patrollerId];
      return (
        // biome-ignore lint/complexity/useOptionalChain: leave it
        assignment && assignment.accepted && !assignment.removed
      );
    });
  }, [patrol]);
  // const expectedPatrollers = useMemo(() => {
  //   const patrollers = patrol.patrollers || {};
  //   return Object.keys(patrollers).filter((patrollerId) => {
  //     const assignment = patrollers[patrollerId];
  //     return assignment !== undefined && !assignment.declined && !assignment.removed;
  //   });
  // }, [patrol]);
  const expectedGuests = useMemo(() => {
    const guests = patrol.guests || {};

    return Object.keys(guests).filter((guestId) => {
      const assignment = guests[guestId];
      return assignment !== undefined && !assignment.removed;
    });
  }, [patrol]);

  const startPatrolForm = useFormik<StartPatrolForm>({
    initialValues: {
      guestsPresent: expectedGuests,
      notes: '',
      patrollersPresent: acceptedPatrollers,
      policeEventNumber: patrol.eccLogOnResponse?.policeEventNumber ?? '',
      started: roundTimeToMinute(DateTime.local()).toISO(),
      vehicle: patrol.eccLogOnNotification
        ? !!patrol.eccLogOnNotification.vehicle
        : true,
      vehicleRego:
        (!!patrol.eccLogOnNotification?.vehicle &&
          patrol.eccLogOnNotification.vehicle.rego) ||
        '',
      vehicleStartOdometer: undefined,
    },
    onReset: () => navigate(-1),
    onSubmit: async (values) => {
      if (!values.patrollersPresent.length) {
        enqueueSnackbar(
          'At least one patroller must be present to start a patrol',
          { variant: 'warning' },
        );
        return;
      }
      if (values.patrollersPresent.length + values.guestsPresent.length < 2) {
        enqueueSnackbar(
          'At least two people must be present to start a patrol',
          { variant: 'warning' },
        );
        return;
      }
      const body: StartPatrolRequest = {
        guestsPresent: values.guestsPresent,
        location: values.location,
        notes: values.notes || undefined,
        patrollersPresent: values.patrollersPresent,
        policeEventNumber: values.policeEventNumber || undefined,
        started: values.started
          ? DateTime.fromISO(values.started).toJSDate()
          : new Date(),
        vehicle: values.vehicle
          ? {
              rego: values.vehicleRego,
              startOdometer: values.vehicleStartOdometer ?? 0,
            }
          : undefined,
      };
      try {
        if (body.vehicle) {
          setPreviousVehicles({
            ...recentVehicles
              .map((recent) => previousVehicles[recent.rego])
              .reduce<Record<string, PreviousVehicles>>(
                (acc, item) =>
                  item ? { ...acc, [item.vehicle.rego]: item } : acc,
                {},
              ),
            [body.vehicle.rego.toUpperCase()]: {
              lastUsed: Date.now(),
              vehicle: {
                ...previousVehicles[body.vehicle.rego],
                ...body.vehicle,
              } as unknown as PreviousVehicles['vehicle'],
            },
          });
        }
        await auth.axios.post(
          `/api/v1/patrol/${encodeURIComponent(id)}/start`,
          body,
        );
        enqueueSnackbar('Patrol started', { variant: 'success' });
      } catch (err: unknown) {
        Sentry.captureException(err);
        if (err instanceof Error) {
          enqueueSnackbar(`Unable to update: ${err.message}`, {
            variant: 'error',
          });
        } else {
          throw err;
        }
      }
      refresh();
    },
    validate: async (value) => {
      const { vehicle, vehicleRego, vehicleStartOdometer, ...rest } = value;
      const a = vehicle
        ? formikCheck(startPatrolFormContract, {
            vehicleRego,
            vehicleStartOdometer,
            ...rest,
          })
        : formikCheck(startPatrolFormContractNoVehicle, rest);
      console.log(await a);
      return a;
    },
    validateOnBlur: true,
  });

  const handlePreviousVehicleClick = (
    vehicle: Patrol.PatrolVehicleInfo,
  ): void => {
    startPatrolForm
      .setValues({
        ...startPatrolForm.values,
        vehicle: true,
        vehicleRego: vehicle.rego,
      })
      .catch((error) => {
        enqueueSnackbar('Error selecting previous vehicle', {
          variant: 'error',
        });
        Sentry.captureException(error);
      });
  };

  const handlePreviousVehicleDelete = (
    vehicle: Patrol.PatrolVehicleInfo & { fromRegister?: boolean },
  ): void => {
    // Only allow deleting recently used vehicles, not register vehicles
    if (vehicle.fromRegister) {
      return;
    }
    setPreviousVehicles({
      ...recentVehicles
        .map((recent) => previousVehicles[recent.rego])
        .reduce<Record<string, PreviousVehicles>>(
          (acc, item) =>
            item && item.vehicle.rego !== vehicle.rego
              ? { ...acc, [item.vehicle.rego]: item }
              : acc,
          {},
        ),
    });
  };

  return (
    <StartPatrolView
      locationAvailable={isGeolocationEnabled && isGeolocationAvailable}
      onRecentVehicleClick={handlePreviousVehicleClick}
      onRecentVehicleDelete={handlePreviousVehicleDelete}
      patrol={patrol}
      recentVehicles={combinedVehicles}
      refresh={refresh}
      startPatrolForm={startPatrolForm}
    />
  );
};
